"use client";

import { FC } from "react";
import styles from "./styles/SchoolFeedSearchFilter.module.scss";
import { observer } from "mobx-react";
import classNames from "classnames";
import { useStores } from "../../../stores/useStores";
import { MobileNewsWidgetLink } from "../../schoolfeed/widgets/NewsWidget";
import { MobileEventsWidgetLink } from "../../schoolfeed/EventsWidget";
import { SchoolFeedMobileWidgetLink } from "../../schoolfeed/SchoolFeedMobileWidgetLink";
import CourseworkIcon from "../../../assets/images/coursework.png";
import FilesIcon from "../../../assets/images/files.png";
import MediaIcon from "../../../assets/images/media.png";
import { withLocaleInURI } from "../../../internationalization/i18nURI";
import { useSchoolFeedUrlQuery } from "../../../hooks/useSchoolFeedData";
import {
  isSchoolBlocksApp,
  isSchoolFeedStandalone,
} from "../../../utils/SchoolBlocksUtilities";
import { useSchoolFeedTitle } from "../../../hooks/useSchoolFeedTitle";
import useSWR from "swr/immutable";
import WatsonApi from "../../../backends/WatsonApi";

interface SchoolFeedAnalyticsQuery {
  organization_id?: string;
  following?: string;
  published?: boolean;
}

export function useSchoolFeedData() {
  const { organizationStore, userStore } = useStores();
  const urlQuery = useSchoolFeedUrlQuery();

  function buildKey() {
    if (
      organizationStore.currentOrganization.type === "collection" &&
      organizationStore.currentOrganization.collectionRules?.some(
        (rule) => rule.collection_of === "people",
      )
    ) {
      return null;
    }
    return `getAnalytics?include_everything=${urlQuery.orgId}${urlQuery.following}`;
  }

  const {
    data: contentCounts,
    isLoading,
    isValidating,
  } = useSWR(buildKey(), async () => {
    const client = await WatsonApi();
    const q: SchoolFeedAnalyticsQuery = {
      following: urlQuery.following,
    };
    if (!urlQuery.orgId && !urlQuery.following) {
      q["organization_id"] = organizationStore.organization.id;
    } else if (!urlQuery.following) {
      q["organization_id"] = urlQuery.orgId;
    }
    if (!userStore.editor) {
      q["published"] = true;
    }

    let response;
    if (urlQuery.orgId) {
      response =
        await client.apis.organizations.organizations_schoolfeed_analytics_list(
          {
            organization_pk: urlQuery.orgId,
            ...q,
          },
        );
    } else {
      response =
        await client.apis.schoolfeed_analytics.schoolfeed_analytics_list(q);
    }

    const analytics = response.obj[0];
    return {
      news: analytics.has_news,
      events: analytics.has_event,
      files: analytics.has_file,
      media: analytics.has_media,
      coursework: analytics.has_coursework,
    };
  });

  return {
    contentCounts: contentCounts,
    isLoadingContentCounts: isLoading || isValidating,
    urlQuery,
  };
}

const SchoolFeedSearchFilters: FC<{
  className?: string;
}> = observer((props) => {
  const { contentCounts, urlQuery } = useSchoolFeedData();

  const { i18nStore } = useStores();

  const title = useSchoolFeedTitle();
  const schoolFeedStandalone = isSchoolFeedStandalone();

  const containerClassName = classNames({
    [styles.container]: true,
    [styles.containerCleverFeed]: schoolFeedStandalone,
    [props.className || ""]: props.className,
  });

  return (
    <div className={containerClassName}>
      {isSchoolBlocksApp() && (
        <div className={styles.title}>
          <h2>{title}</h2>
        </div>
      )}
      <div className={styles.mobileNav}>
        <MobileNewsWidgetLink
          active={
            urlQuery.currentView === "content" && urlQuery.type === "news"
          }
        />
        <MobileEventsWidgetLink
          active={
            urlQuery.currentView === "content" && urlQuery.type === "events"
          }
        />
        {contentCounts?.coursework && (
          <SchoolFeedMobileWidgetLink
            active={
              urlQuery.currentView === "content" &&
              urlQuery.type === "coursework"
            }
            src={CourseworkIcon}
            text={"Coursework"}
            href={{
              pathname: withLocaleInURI(
                "/feed",
                i18nStore.currentAppSupportedLocale,
              ),
              query: { ...urlQuery, type: "coursework" },
            }}
          />
        )}
        {contentCounts?.files && (
          <SchoolFeedMobileWidgetLink
            active={
              urlQuery.currentView === "content" && urlQuery.type === "files"
            }
            src={FilesIcon}
            text={"Files & Folders"}
            href={{
              pathname: withLocaleInURI(
                "/feed",
                i18nStore.currentAppSupportedLocale,
              ),
              query: { ...urlQuery, type: "files" },
            }}
          />
        )}
        {contentCounts?.media && (
          <SchoolFeedMobileWidgetLink
            active={
              urlQuery.currentView === "content" && urlQuery.type === "media"
            }
            src={MediaIcon}
            text={"Media"}
            href={{
              pathname: withLocaleInURI(
                "/feed",
                i18nStore.currentAppSupportedLocale,
              ),
              query: { ...urlQuery, type: "media" },
            }}
          />
        )}
      </div>
    </div>
  );
});

export default SchoolFeedSearchFilters;
